(() => {
  const MESSAGE_SELECTOR = '[data-ugb-gift-fields]';
  const DIGITAL_SELECTOR = '[data-ugb-instant-gift]';
  const CUSTOM_OCCASION_VALUE = '__custom__';
  const MESSAGE_MAX = 250;
  const SIGNATURE_MAX = 45;
  const drafts = new Map();

  function clamp(value, max) {
    return String(value ?? '').slice(0, max);
  }

  function localTodayISO() {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  }

  function randomToken() {
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const values = new Uint32Array(2);
      window.crypto.getRandomValues(values);
      return Array.from(values, (value) => value.toString(36)).join('');
    }
    return Math.random().toString(36).slice(2, 12);
  }

  function buildGiftMessageId() {
    return `IGM-${Date.now().toString(36)}-${randomToken()}`.toUpperCase();
  }

  function draftFor(element) {
    const key = element.dataset.ugbProductId || 'default';
    if (!drafts.has(key)) drafts.set(key, {});
    return drafts.get(key);
  }

  function initializeMessageFields(root) {
    if (!(root instanceof HTMLElement) || root.dataset.ugbMessageReady === 'true') return;

    const occasion = root.querySelector('[data-ugb-occasion]');
    const occasionSelect = root.querySelector('[data-ugb-occasion-select]');
    const occasionCustom = root.querySelector('[data-ugb-occasion-custom]');
    const message = root.querySelector('[data-ugb-message]');
    const signature = root.querySelector('[data-ugb-signature]');
    const messageCount = root.querySelector('[data-ugb-message-count]');
    const signatureCount = root.querySelector('[data-ugb-signature-count]');

    if (
      !(occasion instanceof HTMLInputElement) ||
      !(occasionSelect instanceof HTMLSelectElement) ||
      !(occasionCustom instanceof HTMLInputElement) ||
      !(message instanceof HTMLTextAreaElement) ||
      !(signature instanceof HTMLInputElement)
    ) return;

    const draft = draftFor(root);
    const standardOccasions = Array.from(occasionSelect.options)
      .map((option) => option.value)
      .filter((value) => value && value !== CUSTOM_OCCASION_VALUE);

    if (draft.occasion !== undefined) occasion.value = draft.occasion;
    if (draft.message !== undefined) message.value = clamp(draft.message, MESSAGE_MAX);
    if (draft.signature !== undefined) signature.value = clamp(draft.signature, SIGNATURE_MAX);

    const updateCounters = () => {
      if (messageCount) messageCount.textContent = `${message.value.length} / ${MESSAGE_MAX}`;
      if (signatureCount) signatureCount.textContent = `${signature.value.length} / ${SIGNATURE_MAX}`;
    };

    const restoreOccasionUi = () => {
      const value = occasion.value || '';
      if (!value) {
        occasionSelect.value = '';
        occasionCustom.value = '';
      } else if (standardOccasions.includes(value)) {
        occasionSelect.value = value;
        occasionCustom.value = '';
      } else {
        occasionSelect.value = CUSTOM_OCCASION_VALUE;
        occasionCustom.value = value;
      }
    };

    const save = () => {
      if (message.value.length > MESSAGE_MAX) message.value = message.value.slice(0, MESSAGE_MAX);
      if (signature.value.length > SIGNATURE_MAX) signature.value = signature.value.slice(0, SIGNATURE_MAX);
      updateCounters();
      draft.occasion = occasion.value;
      draft.message = message.value;
      draft.signature = signature.value;
    };

    const syncOccasion = () => {
      const isCustom = occasionSelect.value === CUSTOM_OCCASION_VALUE;
      occasionCustom.hidden = !isCustom;
      occasionCustom.disabled = !isCustom;

      if (isCustom) {
        occasion.value = occasionCustom.value;
      } else {
        occasion.value = occasionSelect.value;
      }

      save();
    };

    restoreOccasionUi();
    syncOccasion();
    updateCounters();

    occasionSelect.addEventListener('change', () => {
      syncOccasion();
      if (occasionSelect.value === CUSTOM_OCCASION_VALUE) occasionCustom.focus();
    });
    occasionCustom.addEventListener('input', syncOccasion);
    message.addEventListener('input', save);
    signature.addEventListener('input', save);
    root.dataset.ugbMessageReady = 'true';
  }

  function initializeDigitalCard(card) {
    if (!(card instanceof HTMLElement) || card.dataset.ugbReady === 'true') return;

    const toggle = card.querySelector('[data-ugb-instant-toggle]');
    const fields = card.querySelector('[data-ugb-instant-fields]');
    const email = card.querySelector('[data-ugb-recipient-email]');
    const date = card.querySelector('[data-ugb-delivery-date]');
    const id = card.querySelector('[data-ugb-gift-message-id]');

    if (!(toggle instanceof HTMLInputElement) || !fields || !(email instanceof HTMLInputElement) || !(date instanceof HTMLInputElement) || !(id instanceof HTMLInputElement)) return;

    const draft = draftFor(card);
    if (draft.instantEnabled !== undefined) toggle.checked = draft.instantEnabled;
    if (draft.recipientEmail !== undefined) email.value = draft.recipientEmail;
    if (draft.deliveryDate !== undefined) date.value = draft.deliveryDate;
    if (draft.giftMessageId !== undefined) id.value = draft.giftMessageId;

    const save = () => {
      draft.instantEnabled = toggle.checked;
      draft.recipientEmail = email.value;
      draft.deliveryDate = date.value;
      draft.giftMessageId = id.value;
    };

    const syncState = () => {
      const active = toggle.checked;
      const today = localTodayISO();

      card.classList.toggle('is-active', active);
      fields.hidden = !active;
      toggle.setAttribute('aria-expanded', active ? 'true' : 'false');

      email.disabled = !active;
      date.disabled = !active;
      id.disabled = !active;
      email.required = active;
      date.required = active;
      date.min = today;

      if (active && !id.value) id.value = buildGiftMessageId();

      if (date.value && date.value < today) {
        date.setCustomValidity('Please choose today or a future date.');
      } else {
        date.setCustomValidity('');
      }

      save();
    };

    toggle.addEventListener('change', syncState);
    email.addEventListener('input', save);
    date.addEventListener('focus', syncState);
    date.addEventListener('change', syncState);
    card.dataset.ugbReady = 'true';
    syncState();
  }

  function initializeAll(root = document) {
    if (root instanceof HTMLElement && root.matches(MESSAGE_SELECTOR)) initializeMessageFields(root);
    if (root instanceof HTMLElement && root.matches(DIGITAL_SELECTOR)) initializeDigitalCard(root);
    root.querySelectorAll?.(MESSAGE_SELECTOR).forEach(initializeMessageFields);
    root.querySelectorAll?.(DIGITAL_SELECTOR).forEach(initializeDigitalCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) initializeAll(node);
      }
    }
  });

  if (document.documentElement) observer.observe(document.documentElement, { childList: true, subtree: true });
})();
